import {
  ReportCoordinatesDto,
  useReportsFindCoordinates,
  useReportsFindOne,
} from '@api/WhisApi/generated';
import { LoadingView } from '@components/LoadingView/LoadingView';
import ProjectMap from '@components/MapView/ProjectMap';
import {
  ExtendedReportFiltersParams,
  ReportFiltersValues,
} from '@components/ReportFilters/reportFilters.types';
import { buildReportFiltersParams } from '@components/ReportFilters/reportFilters.utils';
import ReportCard from '@components/ReportList/ReportCard/ReportCard';
import { mapReportToSummary } from '@components/ReportList/utils/mapReportToSummary';
import { useIsWeb } from '@hooks/useIsWeb';
import ReportDetail from '@screens/ReportDetail/components/ReportDetail';
import { useRouter, useSegments } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XStack } from 'tamagui';

interface MapScreenProps {
  filters?: ReportFiltersValues;
}

export default function MapScreen({
  filters,
}: MapScreenProps): React.ReactNode {
  const { i18n } = useTranslation();
  const isWeb = useIsWeb();
  const router = useRouter();
  const segments = useSegments() as string[];
  const isBoardRoute = segments.includes('board');
  const reportDetailBase = isBoardRoute ? '/board/reports' : '/reports';

  const [selectedReport, setSelectedReport] = useState<string>('');

  const filterParams: Partial<ExtendedReportFiltersParams> = filters
    ? buildReportFiltersParams(filters)
    : {};

  const { data, isPending } = useReportsFindCoordinates({
    myReports: false,
    ...filterParams,
  });

  const { data: report, isPending: selectedReportIsPending } =
    useReportsFindOne(selectedReport, {
      query: { enabled: !!selectedReport },
    });

  const reports: ReportCoordinatesDto[] = data?.reports || [];

  const handleSelected = (item?: ReportCoordinatesDto): void => {
    if (!item) {
      return setSelectedReport('');
    }
    if (item.id) setSelectedReport(item.id);
  };

  const handleReportDetail = (): void => {
    router.push(`${reportDetailBase}/${selectedReport}`);
  };

  const renderPopup = (): React.ReactElement | undefined => {
    if (selectedReportIsPending) return <LoadingView />;
    if (!report) return undefined;
    return isWeb ? (
      <ReportDetail report={report} defaultOpen={true} />
    ) : (
      <ReportCard
        report={mapReportToSummary(report, i18n)}
        backgroundColor="white"
      />
    );
  };

  if (isPending) {
    return <LoadingView />;
  }

  return (
    <XStack flex={1}>
      <ProjectMap<ReportCoordinatesDto>
        items={reports
          .filter(
            (report) =>
              report.longitude !== undefined && report.latitude !== undefined,
          )
          .map((report) => ({
            latitude: report.latitude!,
            longitude: report.longitude!,
            item: report,
          }))}
        popup={selectedReport ? renderPopup() : undefined}
        onSelectItem={handleSelected}
        onDetailItem={handleReportDetail}
      />
    </XStack>
  );
}
